import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  formatDate,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminParticipantsQueryKey,
  useDeleteAdminPlanEnrollment,
  useGetAdminAdvisoryOrganization,
  useGetEnrollmentDetail,
  useGetPlan,
  usePostAdminForceOutReprocess,
  usePostAdminMapReanalysis,
  usePostAdminParticipantLiteSyncForParticipant,
  usePostAdminParticipantSync,
  usePostAdminRemoveFromMap,
  usePostAdminSsnReset
} from '@ijoin/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Avatar,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { useToggle } from 'usehooks-ts';

import {
  EnrollmentActivitySection,
  EnrollmentAnnuityPoliciesSection,
  EnrollmentChangesSection,
  EnrollmentFundBalancesSection,
  EnrollmentInfoSection,
  EnrollmentMapSection,
  EnrollmentRkTransactionExportsSection,
  EnrollmentRkTransactionsSection,
  EnrollmentSubmissionLogSection,
  MapDefaultDialog,
  MapOptOutDialog,
  QueryBoundary
} from '../../../components';

const Page = () => {
  const { advisoryOrganizationId, planEnrollmentId, planId } =
    Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isDemoResetOpen, toggleDemoResetOpen] = useToggle();
  const [isForceOutOpen, toggleForceOutOpen] = useToggle();
  const [isLiteSyncOpen, toggleLiteSyncOpen] = useToggle();
  const [isMapDefaultOpen, toggleMapDefaultOpen] = useToggle();
  const [isMapOptOutOpen, toggleMapOptOutOpen] = useToggle();
  const [isMapReanalysisOpen, toggleMapReanalysisOpen] = useToggle();
  const [isRemoveFromMapOpen, toggleRemoveFromMapOpen] = useToggle();
  const [isSyncOpen, toggleSyncOpen] = useToggle();

  const detail = useGetEnrollmentDetail(planId, planEnrollmentId);

  const plan = useGetPlan(planId);

  const org = useGetAdminAdvisoryOrganization(advisoryOrganizationId);

  const forceOutReprocess = usePostAdminForceOutReprocess({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger force out reprocess'),
      onSuccess: () =>
        notifications.success('Force out reprocess triggered successfully')
    }
  });

  const mapReanalysis = usePostAdminMapReanalysis({
    mutation: {
      onError: () => notifications.error('Failed to trigger MAP reanalysis'),
      onSuccess: () =>
        notifications.success('MAP reanalysis triggered successfully')
    }
  });

  const participantLiteSync = usePostAdminParticipantLiteSyncForParticipant({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger participant lite sync'),
      onSuccess: () =>
        notifications.success('Participant lite sync triggered successfully')
    }
  });

  const participantSync = usePostAdminParticipantSync({
    mutation: {
      onError: () => notifications.error('Failed to sync the participant'),
      onSuccess: () => {
        queryClient.invalidateQueries();
        notifications.success('Participant synced successfully');
      }
    }
  });

  const ssnReset = usePostAdminSsnReset({
    mutation: {
      onError: () => notifications.error('Failed to run demo reset'),
      onSuccess: () => {
        queryClient.invalidateQueries();
        notifications.success('Demo reset completed successfully');
      }
    }
  });

  const removeFromMap = usePostAdminRemoveFromMap({
    mutation: {
      onError: () => notifications.error('Failed to remove from MAP'),
      onSuccess: () => {
        queryClient.invalidateQueries();
        notifications.success('Participant removed from MAP successfully');
      }
    }
  });

  const deleteEnrollment = useDeleteAdminPlanEnrollment({
    mutation: {
      onError: () => notifications.error('Failed to delete enrollment'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminParticipantsQueryKey()
        });
        notifications.success('Enrollment deletion initiated');
        navigate({
          params: { advisoryOrganizationId, planId },
          to: '/organizations/$advisoryOrganizationId/plans/$planId/participants'
        });
      }
    }
  });

  const onConfirmSync = () => {
    toggleSyncOpen();

    if (detail.data?.participantId) {
      participantSync.mutate({
        participantId: detail.data.participantId,
        planId
      });
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Link
        params={{ advisoryOrganizationId, planId }}
        style={{ alignItems: 'center', display: 'flex', gap: 4 }}
        to='/organizations/$advisoryOrganizationId/plans/$planId/participants'>
        <ArrowBackIcon />
        Participants
      </Link>

      {detail.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'
              sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack alignItems='center' direction='row' spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    height: 48,
                    width: 48
                  }}>
                  {detail.data.firstName[0]}
                  {detail.data.lastName[0]}
                </Avatar>
                <div>
                  <Typography variant='h3'>
                    {detail.data.firstName} {detail.data.lastName}
                  </Typography>
                  {detail.data.inMap && (
                    <Stack direction='row' spacing={1} sx={{ mt: 0.5 }}>
                      <StatusBadge severity='success'>MAP ENROLLED</StatusBadge>
                    </Stack>
                  )}
                </div>
              </Stack>

              <Stack
                alignItems='center'
                direction='row'
                spacing={3}
                sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Plan
                  </Typography>
                  <Typography variant='subtitle1'>
                    <Link
                      params={{ advisoryOrganizationId, planId }}
                      to='/organizations/$advisoryOrganizationId/plans/$planId'>
                      {plan.data?.description ?? planId.slice(0, 8)}
                    </Link>
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Advisory Organization
                  </Typography>
                  <Typography variant='subtitle1'>
                    <Link
                      params={{ advisoryOrganizationId }}
                      to='/organizations/$advisoryOrganizationId'>
                      {org.data?.name ?? advisoryOrganizationId.slice(0, 8)}
                    </Link>
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Plan ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {planId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={planId} />
                  </Stack>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Enrollment ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {planEnrollmentId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={planEnrollmentId} />
                  </Stack>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Participant ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {(detail.data.participantId ?? '').slice(0, 8)}…
                    </Typography>
                    <CopyableText value={detail.data.participantId ?? ''} />
                  </Stack>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Last Sync
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace' }} variant='body2'>
                    {detail.data.syncDate
                      ? formatDate(detail.data.syncDate, {
                          format: 'MM/DD/YYYY h:mm A'
                        })
                      : ''}
                  </Typography>
                </div>
                <ActionMenu
                  actions={[
                    detail.data.hasActiveMapProgram &&
                      !detail.data.inMap && {
                        content: 'Default into MAP',
                        onSelect: toggleMapDefaultOpen
                      },
                    {
                      content: 'Delete Enrollment',
                      onSelect: toggleDeleteOpen
                    },
                    detail.data.socialSecurityNumber && {
                      content: 'Demo Reset',
                      onSelect: toggleDemoResetOpen
                    },
                    {
                      content: 'Download Debug Snapshot',
                      onSelect: () =>
                        window.open(
                          `/api/v3/admin/plans/${planId}/enrollments/${planEnrollmentId}/debug-snapshot`,
                          '_blank'
                        )
                    },
                    plan.data?.isSyncable && {
                      content: 'Full Participant Sync',
                      onSelect: toggleSyncOpen
                    },
                    detail.data.participantId && {
                      content: 'Impersonate',
                      onSelect: () =>
                        window.open(
                          `/api/v3/plans/${planId}/participants/${detail.data.participantId}/impersonate`,
                          '_blank'
                        )
                    },
                    detail.data.entryExitTypeCode &&
                      !['O', 'P'].includes(detail.data.entryExitTypeCode) && {
                        content: 'Opt Out of MAP',
                        onSelect: toggleMapOptOutOpen
                      },
                    plan.data?.isRelius && {
                      content: 'Participant Lite Sync',
                      onSelect: toggleLiteSyncOpen
                    },
                    detail.data?.inMap && {
                      content: 'Remove from MAP',
                      onSelect: toggleRemoveFromMapOpen
                    },
                    plan.data?.enableAutoForceOut && {
                      content: 'Trigger Force Out',
                      onSelect: toggleForceOutOpen
                    },
                    detail.data?.inMap && {
                      content: 'Trigger MAP Reanalysis',
                      onSelect: toggleMapReanalysisOpen
                    }
                  ].filter(Boolean)}
                  trigger={
                    <IconButton data-testid='participantActionsButton'>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
              </Stack>
            </Stack>
          </CardContent>
          <ConfirmationDialog
            confirmColor='error'
            confirmText='Reset'
            data-testid='ssnResetParticipant'
            isOpen={isDemoResetOpen}
            message={`This will unfinalize enrollments, delete export queue entries, and delete allocations for SSN ${detail.data.socialSecurityNumber} within this plan.`}
            onCancel={toggleDemoResetOpen}
            onConfirm={() => {
              toggleDemoResetOpen();
              ssnReset.mutate({
                data: { planEnrollmentId },
                planId
              });
            }}
            title='Demo Reset'
          />
          <ConfirmationDialog
            confirmText='Sync'
            data-testid='participantLiteSync'
            isOpen={isLiteSyncOpen}
            message={`This will trigger a participant lite sync for ${detail.data.firstName} ${detail.data.lastName}.`}
            onCancel={toggleLiteSyncOpen}
            onConfirm={() => {
              toggleLiteSyncOpen();
              if (detail.data?.participantId) {
                participantLiteSync.mutate({
                  participantId: detail.data.participantId,
                  planId
                });
              }
            }}
            title='Participant Lite Sync'
          />
          <ConfirmationDialog
            confirmText='Sync'
            data-testid='participantSync'
            isOpen={isSyncOpen}
            message={`This will pull ${detail.data.firstName} ${detail.data.lastName} fresh from the record keeper, bypassing the 24 hour freshness window. It can take a moment to complete.`}
            onCancel={toggleSyncOpen}
            onConfirm={onConfirmSync}
            title='Full Participant Sync'
          />
          <ConfirmationDialog
            confirmationText='FORCE OUT'
            confirmColor='error'
            confirmText='Trigger'
            data-testid='forceOutReprocess'
            isOpen={isForceOutOpen}
            message={`This will trigger automatic force out processing for ${detail.data.firstName} ${detail.data.lastName}. The participant will be added to the force out queue.`}
            onCancel={toggleForceOutOpen}
            onConfirm={() => {
              toggleForceOutOpen();
              forceOutReprocess.mutate({
                planEnrollmentId,
                planId
              });
            }}
            title='Trigger Force Out'
          />
          <ConfirmationDialog
            confirmText='Trigger'
            data-testid='mapReanalysis'
            isOpen={isMapReanalysisOpen}
            message={`This will trigger MAP reanalysis for ${detail.data.firstName} ${detail.data.lastName}. Their managed account allocation will be recalculated.`}
            onCancel={toggleMapReanalysisOpen}
            onConfirm={() => {
              toggleMapReanalysisOpen();

              if (detail.data?.participantId) {
                mapReanalysis.mutate({
                  participantId: detail.data.participantId,
                  planId
                });
              }
            }}
            title='Trigger MAP Reanalysis'
          />
          <ConfirmationDialog
            confirmationText='DELETE'
            confirmColor='error'
            confirmText='Delete'
            data-testid='deleteEnrollment'
            isOpen={isDeleteOpen}
            message={`This will permanently delete the enrollment for ${detail.data.firstName} ${detail.data.lastName} and all associated data. This action is irreversible.`}
            onCancel={toggleDeleteOpen}
            onConfirm={() => {
              toggleDeleteOpen();
              deleteEnrollment.mutate({
                planEnrollmentId,
                planId
              });
            }}
            title='Delete Enrollment'
          />
          <ConfirmationDialog
            confirmationText='REMOVE'
            confirmColor='error'
            confirmText='Remove'
            data-testid='removeFromMap'
            isOpen={isRemoveFromMapOpen}
            message={`This will remove ${detail.data.firstName} ${detail.data.lastName} from the Managed Account Program. Their MAP balances will be deleted and enrollment path will be cleared. This action is irreversible.`}
            onCancel={toggleRemoveFromMapOpen}
            onConfirm={() => {
              toggleRemoveFromMapOpen();
              removeFromMap.mutate({
                planEnrollmentId,
                planId
              });
            }}
            title='Remove from MAP'
          />
          {isMapDefaultOpen && (
            <MapDefaultDialog
              onClose={toggleMapDefaultOpen}
              participantName={`${detail.data.firstName} ${detail.data.lastName}`}
              planEnrollmentId={planEnrollmentId}
            />
          )}
          {isMapOptOutOpen && (
            <MapOptOutDialog
              onClose={toggleMapOptOutOpen}
              participantName={`${detail.data.firstName} ${detail.data.lastName}`}
              planEnrollmentId={planEnrollmentId}
            />
          )}
        </Card>
      )}

      <EnrollmentInfoSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <QueryBoundary>
        <EnrollmentChangesSection
          planEnrollmentId={planEnrollmentId}
          planId={planId}
        />
      </QueryBoundary>

      {detail.data?.inMap && (
        <EnrollmentMapSection
          planEnrollmentId={planEnrollmentId}
          planId={planId}
        />
      )}

      <EnrollmentFundBalancesSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <EnrollmentAnnuityPoliciesSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <EnrollmentRkTransactionsSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <EnrollmentRkTransactionExportsSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <EnrollmentSubmissionLogSection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />

      <EnrollmentActivitySection
        planEnrollmentId={planEnrollmentId}
        planId={planId}
      />
    </Stack>
  );
};

export const Route = createFileRoute(
  '/organizations/$advisoryOrganizationId/plans/$planId_/participants/$planEnrollmentId'
)({
  component: Page
});
